import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { serviceMappingsApi, customersApi, billingApi, partnerCenterApi } from '../services/api';
import { CustomerServicesView, ServiceMapping, ServiceProduct, ServiceType, Customer } from '../types';
import toast from 'react-hot-toast';
import api from '../services/api';
import './CustomerServices.css';

const SERVICE_LABELS: Record<ServiceType, string> = {
  xero: 'Xero',
  microsoft365: 'Microsoft 365',
  cloudblue: 'CloudBlue',
  cove: 'Cove Backup',
  sentinelone: 'SentinelOne',
  proxmox: 'Proxmox',
  whmcs: 'WHMCS',
  splynx: 'Splynx',
  manual: 'Manual services'
};

const SERVICE_COLORS: Record<ServiceType, string> = {
  xero: '#13B5EA',
  microsoft365: '#00A4EF',
  cloudblue: '#6366F1',
  cove: '#10B981',
  sentinelone: '#8B5CF6',
  proxmox: '#F59E0B',
  whmcs: '#EF4444',
  splynx: '#0D9488',
  manual: '#64748B'
};

const BILLING_CYCLES = ['monthly', 'quarterly', 'semi-annually', 'annually', 'one time'];
const EMPTY_MANUAL_FORM = { product_name: '', product_sku: '', quantity: 1, unit_price: '', billing_cycle: 'monthly' };

function CustomerServices() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [servicesView, setServicesView] = useState<CustomerServicesView | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState<number | null>(null); // mapping ID
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType>('microsoft365');
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  
  // Form states
  const [mappingForm, setMappingForm] = useState({
    service_type: 'microsoft365' as ServiceType,
    external_id: '',
    external_name: '',
  });
  
  const [productForm, setProductForm] = useState({
    product_name: '',
    product_sku: '',
    quantity: 1,
    unit_price: '',
    billing_cycle: 'monthly',
  });

  // Aliases (moved from the Invoice Prep quick view)
  const [showAddAlias, setShowAddAlias] = useState(false);
  const [aliasForm, setAliasForm] = useState({ aliasName: '', dataSource: 'cloudblue' });

  // Manual services (dedicated CRUD — billing endpoints auto-create the mapping)
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({ ...EMPTY_MANUAL_FORM });
  const [editingManualId, setEditingManualId] = useState<number | null>(null);

  // M365 licensed users (moved from the Invoice Prep quick view)
  const [licensedUsers, setLicensedUsers] = useState<any>(null);
  const [licensedUsersLoading, setLicensedUsersLoading] = useState(false);
  const [expandedSku, setExpandedSku] = useState<string | null>(null);

  useEffect(() => {
    if (customerId) {
      loadData(parseInt(customerId));
    }
  }, [customerId]);

  const loadData = async (id: number) => {
    setLoading(true);
    try {
      const [customerData, servicesData] = await Promise.all([
        customersApi.get(id),
        serviceMappingsApi.getCustomerServices(id)
      ]);
      setCustomer(customerData);
      setServicesView(servicesData);

      // Load CloudBlue comparison if customer has both CloudBlue and M365 mappings
      const services = servicesData?.services || {};
      const cbMappings = services.cloudblue || [];
      const m365Mappings = services.microsoft365 || [];
      if (cbMappings.length > 0 || m365Mappings.length > 0) {
        loadComparison(cbMappings, m365Mappings);
      }
    } catch (error) {
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async (cbMappings: any[], m365Mappings: any[]) => {
    setLoadingComparison(true);
    try {
      // If we have a CloudBlue mapping, fetch comparison from the API
      if (cbMappings.length > 0) {
        const cbExternalId = cbMappings[0].external_id;
        if (cbExternalId) {
          const response = await api.get(`/cloudblue/customer/${cbExternalId}/comparison`);
          if (response.data?.success) {
            setComparisonData(response.data);
            return;
          }
        }
      }
      // Fallback: build comparison from existing service products
      const cbProducts = cbMappings.flatMap((m: any) => m.products || []);
      const m365Products = m365Mappings.flatMap((m: any) => m.products || []);
      if (cbProducts.length > 0 || m365Products.length > 0) {
        setComparisonData({
          cloudblue: {
            items: cbProducts.map((p: any) => ({
              name: p.product_name,
              quantity: p.quantity,
              total_price: p.unit_price,
            })),
            total_qty: cbProducts.reduce((s: number, p: any) => s + (p.quantity || 0), 0),
            total_price: cbProducts.reduce((s: number, p: any) => s + (parseFloat(p.unit_price) || 0), 0),
            currency: 'ZAR',
          },
          m365: {
            items: m365Products.map((p: any) => ({
              name: p.product_name,
              quantity: p.quantity,
            })),
            total_qty: m365Products.reduce((s: number, p: any) => s + (p.quantity || 0), 0),
          },
          is_mapped: true,
        });
      }
    } catch (err) {
      console.error('Failed to load comparison:', err);
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleAddMapping = async () => {
    if (!customerId || !mappingForm.external_name.trim()) {
      toast.error('Please enter a name for this service');
      return;
    }

    try {
      await serviceMappingsApi.create({
        customer_id: parseInt(customerId),
        service_type: mappingForm.service_type,
        external_id: mappingForm.external_id || undefined,
        external_name: mappingForm.external_name,
      });
      toast.success('Service mapping added');
      setShowAddMapping(false);
      setMappingForm({ service_type: 'microsoft365', external_id: '', external_name: '' });
      loadData(parseInt(customerId));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add service mapping');
    }
  };

  const handleAddProduct = async (mappingId: number) => {
    if (!productForm.product_name.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    try {
      await serviceMappingsApi.addProduct(mappingId, {
        product_name: productForm.product_name,
        product_sku: productForm.product_sku || undefined,
        quantity: productForm.quantity,
        unit_price: productForm.unit_price || undefined,
        billing_cycle: productForm.billing_cycle,
      });
      toast.success('Product added');
      setShowAddProduct(null);
      setProductForm({ product_name: '', product_sku: '', quantity: 1, unit_price: '', billing_cycle: 'monthly' });
      if (customerId) loadData(parseInt(customerId));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    }
  };

  const handleDeleteMapping = async (mappingId: number) => {
    if (!window.confirm('Delete this service mapping and all its products?')) return;
    
    try {
      await serviceMappingsApi.delete(mappingId);
      toast.success('Service mapping deleted');
      if (customerId) loadData(parseInt(customerId));
    } catch (error) {
      toast.error('Failed to delete service mapping');
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!window.confirm('Delete this product?')) return;

    try {
      await serviceMappingsApi.deleteProduct(productId);
      toast.success('Product deleted');
      if (customerId) loadData(parseInt(customerId));
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // ── Aliases ──
  const handleAddAlias = async () => {
    if (!customerId) return;
    if (!aliasForm.aliasName.trim()) {
      toast.error('Please enter an alias name');
      return;
    }
    try {
      await customersApi.createAlias(parseInt(customerId), aliasForm.aliasName, aliasForm.dataSource);
      toast.success('Alias added');
      setAliasForm({ aliasName: '', dataSource: 'cloudblue' });
      setShowAddAlias(false);
      loadData(parseInt(customerId));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add alias');
    }
  };

  const handleDeleteAlias = async (aliasId: number) => {
    if (!customerId || !window.confirm('Delete this alias?')) return;
    try {
      await customersApi.deleteAlias(aliasId);
      toast.success('Alias deleted');
      loadData(parseInt(customerId));
    } catch {
      toast.error('Failed to delete alias');
    }
  };

  // ── Manual services ──
  const startEditManual = (product: any) => {
    setEditingManualId(product.id);
    setShowManualForm(true);
    setManualForm({
      product_name: product.product_name || '',
      product_sku: product.product_sku || '',
      quantity: product.quantity || 1,
      unit_price: product.unit_price ? String(product.unit_price) : '',
      billing_cycle: product.billing_cycle || 'monthly',
    });
  };

  const submitManual = async () => {
    if (!customerId) return;
    if (!manualForm.product_name.trim()) {
      toast.error('Service name is required');
      return;
    }
    const payload = {
      product_name: manualForm.product_name.trim(),
      product_sku: manualForm.product_sku.trim() || undefined,
      quantity: Number(manualForm.quantity) || 0,
      unit_price: manualForm.unit_price === '' ? undefined : String(manualForm.unit_price),
      billing_cycle: manualForm.billing_cycle,
    };
    try {
      if (editingManualId) {
        await billingApi.updateManualProduct(editingManualId, payload);
        toast.success('Manual service updated');
      } else {
        await billingApi.addManualProduct(parseInt(customerId), payload);
        toast.success('Manual service added');
      }
      setShowManualForm(false);
      setEditingManualId(null);
      setManualForm({ ...EMPTY_MANUAL_FORM });
      loadData(parseInt(customerId));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save manual service');
    }
  };

  const handleDeleteManual = async (productId: number) => {
    if (!customerId || !window.confirm('Delete this manual service?')) return;
    try {
      await billingApi.deleteManualProduct(productId);
      toast.success('Manual service deleted');
      loadData(parseInt(customerId));
    } catch {
      toast.error('Failed to delete manual service');
    }
  };

  // ── M365 licensed users ──
  const loadLicensedUsers = async (tenantId: string, refresh = false) => {
    setLicensedUsersLoading(true);
    try {
      const data = await partnerCenterApi.getLicensedUsers(tenantId, refresh);
      setLicensedUsers(data);
      if (refresh) toast.success('Licensed users refreshed from Partner Center');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load licensed users');
    } finally {
      setLicensedUsersLoading(false);
    }
  };

  if (loading) {
    return <div className="customer-services-page"><div className="loading">Loading...</div></div>;
  }

  if (!customer || !servicesView) {
    return <div className="customer-services-page"><div className="error">Customer not found</div></div>;
  }

  // Types shown in the services grid ('manual' has its own managed section below)
  const serviceTypes: ServiceType[] = ['xero', 'microsoft365', 'cloudblue', 'cove', 'sentinelone', 'proxmox', 'whmcs', 'splynx'];
  // Types a mapping can be created as via the generic modal (backend whitelist)
  const creatableTypes: ServiceType[] = ['xero', 'microsoft365', 'cloudblue', 'cove', 'sentinelone'];
  const m365Tenants = (servicesView.services['microsoft365'] || []).filter((m: any) => m.external_id);
  const manualProducts = (servicesView.services['manual'] || []).flatMap((m: any) => m.products || []);

  return (
    <div className="customer-services-page">
      <div className="page-header">
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/billing')}>
          ← Back to Invoice Prep
        </button>
        <div className="header-content">
          <h1>{customer.xero_name}</h1>
          <p className="page-subtitle">
            {customer.contact_email || 'No email'}
            {customer.xero_customer_id && <span className="xero-id"> • Xero ID: {customer.xero_customer_id}</span>}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-value">{servicesView.total_services}</div>
          <div className="summary-label">Active Services</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{servicesView.total_products}</div>
          <div className="summary-label">Total Products</div>
        </div>
        {serviceTypes.map(type => {
          const hasService = servicesView.services[type]?.length > 0;
          return (
            <div 
              key={type} 
              className={`service-indicator ${hasService ? 'active' : 'inactive'}`}
              style={{ borderColor: hasService ? SERVICE_COLORS[type] : '#e5e7eb' }}
            >
              <span className="service-dot" style={{ backgroundColor: hasService ? SERVICE_COLORS[type] : '#d1d5db' }}></span>
              {SERVICE_LABELS[type]}
            </div>
          );
        })}
      </div>

      {/* Customer profile & aliases */}
      <div className="profile-panel">
        <div className="profile-panel-head">
          <h2 className="section-title">Customer profile & aliases</h2>
          <button className="btn btn-sm btn-outline" onClick={() => setShowAddAlias(!showAddAlias)}>
            {showAddAlias ? 'Cancel' : '＋ Add alias'}
          </button>
        </div>
        <div className="profile-info-row">
          <span><strong>Xero ID:</strong> {customer.xero_customer_id || 'N/A'}</span>
          <span><strong>Email:</strong> {customer.contact_email || 'N/A'}</span>
          <span><strong>Status:</strong> {customer.status}</span>
        </div>
        {showAddAlias && (
          <div className="inline-form">
            <input
              type="text" className="input" placeholder="Alias name from data source *"
              value={aliasForm.aliasName}
              onChange={e => setAliasForm({ ...aliasForm, aliasName: e.target.value })}
            />
            <select
              className="input input-sm" value={aliasForm.dataSource}
              onChange={e => setAliasForm({ ...aliasForm, dataSource: e.target.value })}
            >
              <option value="cloudblue">CloudBlue</option>
              <option value="microsoft365">Microsoft 365</option>
              <option value="cove">Cove</option>
              <option value="sentinelone">SentinelOne</option>
              <option value="whmcs">WHMCS</option>
              <option value="proxmox">Proxmox</option>
            </select>
            <button className="btn btn-sm btn-primary" onClick={handleAddAlias}>Add</button>
          </div>
        )}
        {customer.aliases && customer.aliases.length > 0 ? (
          <div className="alias-chips">
            {customer.aliases.map(alias => (
              <span key={alias.id} className="alias-chip"
                    title={`Source: ${alias.data_source}${alias.mapping_confidence ? ` · confidence: ${alias.mapping_confidence}` : ''}`}>
                {alias.alias_name}
                <em>{alias.data_source}</em>
                <button onClick={() => handleDeleteAlias(alias.id)} title="Delete alias">×</button>
              </span>
            ))}
          </div>
        ) : (
          <p className="muted-note">No aliases — add one when a data source uses a different name for this customer.</p>
        )}
      </div>

      {/* Manual services */}
      <div className="profile-panel">
        <div className="profile-panel-head">
          <h2 className="section-title">Manual services</h2>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              setEditingManualId(null);
              setManualForm({ ...EMPTY_MANUAL_FORM });
              setShowManualForm(!showManualForm);
            }}
          >
            {showManualForm && !editingManualId ? 'Cancel' : '＋ Add service'}
          </button>
        </div>
        {manualProducts.length > 0 ? (
          <table className="products-table">
            <thead>
              <tr><th>Service</th><th>SKU</th><th>Qty</th><th>Unit Price</th><th>Cycle</th><th></th></tr>
            </thead>
            <tbody>
              {manualProducts.map((product: any) => (
                <tr key={product.id}>
                  <td>{product.product_name}</td>
                  <td className="text-muted">{product.product_sku || '-'}</td>
                  <td>{product.quantity}</td>
                  <td>{product.unit_price || '-'}</td>
                  <td className="text-muted">{product.billing_cycle || '-'}</td>
                  <td className="row-actions">
                    <button className="btn-link" onClick={() => startEditManual(product)}>Edit</button>
                    <button className="btn-link btn-danger" onClick={() => handleDeleteManual(product.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted-note">No manual services — add ad-hoc or legacy billing items here so CloudView stays the single source of truth.</p>
        )}
        {showManualForm && (
          <div className="inline-form">
            <input type="text" className="input" placeholder="Service name *"
                   value={manualForm.product_name}
                   onChange={e => setManualForm({ ...manualForm, product_name: e.target.value })} />
            <input type="text" className="input input-sm" placeholder="SKU (optional)"
                   value={manualForm.product_sku}
                   onChange={e => setManualForm({ ...manualForm, product_sku: e.target.value })} />
            <input type="number" min={0} className="input input-xs" placeholder="Qty" title="Quantity"
                   value={manualForm.quantity}
                   onChange={e => setManualForm({ ...manualForm, quantity: Number(e.target.value) })} />
            <input type="text" className="input input-sm" placeholder="Unit price (R)" title="Unit price in ZAR"
                   value={manualForm.unit_price}
                   onChange={e => setManualForm({ ...manualForm, unit_price: e.target.value })} />
            <select className="input input-sm" value={manualForm.billing_cycle}
                    onChange={e => setManualForm({ ...manualForm, billing_cycle: e.target.value })}>
              {BILLING_CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button className="btn btn-sm btn-primary" onClick={submitManual}>
              {editingManualId ? 'Save' : 'Add'}
            </button>
            {editingManualId && (
              <button className="btn btn-sm btn-outline"
                      onClick={() => { setShowManualForm(false); setEditingManualId(null); }}>
                Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {/* Microsoft 365 licensed users */}
      {m365Tenants.length > 0 && (
        <div className="profile-panel">
          <div className="profile-panel-head">
            <h2 className="section-title">Microsoft 365 licensed users</h2>
            {licensedUsers && (
              <button className="btn btn-sm btn-outline" disabled={licensedUsersLoading}
                      onClick={() => loadLicensedUsers(licensedUsers.tenant_id, true)}>
                {licensedUsersLoading ? '⏳ Refreshing…' : '🔄 Refresh from Partner Center'}
              </button>
            )}
          </div>
          {!licensedUsers ? (
            <div className="lu-tenants">
              {m365Tenants.map((t: any) => (
                <button key={t.external_id} className="btn btn-sm btn-outline" disabled={licensedUsersLoading}
                        onClick={() => loadLicensedUsers(t.external_id)}>
                  {licensedUsersLoading ? '⏳ Loading…' : `👥 Show licensed users${m365Tenants.length > 1 ? ` — ${t.external_name || t.external_id}` : ''}`}
                </button>
              ))}
            </div>
          ) : (
            <div className="lu-panel">
              <div className="lu-meta">
                {licensedUsers.licensed_user_count} licensed / {licensedUsers.user_count} users
                {licensedUsers.fetched_at && (
                  <span className="lu-fetched"> · fetched {new Date(licensedUsers.fetched_at).toLocaleString()}{licensedUsers.cached ? ' (cached)' : ''}</span>
                )}
              </div>
              {licensedUsers.skus.length === 0 ? (
                <p className="muted-note">No license assignments found in this tenant.</p>
              ) : (
                licensedUsers.skus.map((sku: any) => (
                  <div key={sku.sku_name} className="lu-sku">
                    <div className="lu-sku-head"
                         onClick={() => setExpandedSku(expandedSku === sku.sku_name ? null : sku.sku_name)}>
                      <span className="lu-sku-name">{expandedSku === sku.sku_name ? '▾' : '▸'} {sku.sku_name}</span>
                      <span className="lu-sku-count">{sku.user_count} user{sku.user_count !== 1 ? 's' : ''}</span>
                    </div>
                    {expandedSku === sku.sku_name && (
                      <div className="lu-users">
                        {sku.users.map((u: any) => (
                          <div key={u.upn} className="lu-user">
                            <span className="lu-user-name">{u.display_name || u.upn}</span>
                            <span className="lu-user-upn">{u.upn}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
              {licensedUsers.unlicensed_users && licensedUsers.unlicensed_users.length > 0 && (
                <div className="lu-unlicensed">{licensedUsers.unlicensed_users.length} user{licensedUsers.unlicensed_users.length !== 1 ? 's' : ''} without licenses</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Service Button */}
      <div className="actions-bar">
        <button className="btn btn-primary" onClick={() => setShowAddMapping(true)}>
          + Add Service Mapping
        </button>
      </div>

      {/* Add Mapping Modal */}
      {showAddMapping && (
        <div className="modal-overlay" onClick={() => setShowAddMapping(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Add Service Mapping</h2>
            <div className="form-group">
              <label>Service Type</label>
              <select
                className="input"
                value={mappingForm.service_type}
                onChange={e => setMappingForm({ ...mappingForm, service_type: e.target.value as ServiceType })}
              >
                {creatableTypes.map(type => (
                  <option key={type} value={type}>{SERVICE_LABELS[type]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Name in External Service</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Customer name as it appears in M365"
                value={mappingForm.external_name}
                onChange={e => setMappingForm({ ...mappingForm, external_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>External ID (optional)</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Tenant ID, Account ID"
                value={mappingForm.external_id}
                onChange={e => setMappingForm({ ...mappingForm, external_id: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleAddMapping}>Add Mapping</button>
              <button className="btn btn-outline" onClick={() => setShowAddMapping(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* License Comparison */}
      {comparisonData && (
        <div className="comparison-section">
          <h2 className="section-title">License Comparison</h2>
          <div className="comparison-grid">
            <div className="comparison-card cloudblue-card">
              <div className="comparison-header cloudblue-header-card">
                <span className="comparison-dot" style={{background: '#6366f1'}}></span>
                CloudBlue (Billed by Tarsus)
              </div>
              {comparisonData.cloudblue?.items?.length > 0 ? (
                <table className="comparison-table">
                  <thead><tr><th>Product</th><th>Qty</th><th>Price</th></tr></thead>
                  <tbody>
                    {comparisonData.cloudblue.items.map((item: any, i: number) => (
                      <tr key={i}>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>{item.total_price ? `R${Number(item.total_price).toFixed(2)}` : '—'}</td>
                      </tr>
                    ))}
                    <tr className="comparison-total">
                      <td><strong>Total</strong></td>
                      <td><strong>{comparisonData.cloudblue.total_qty}</strong></td>
                      <td><strong>R{comparisonData.cloudblue.total_price?.toFixed(2) || '0.00'}</strong></td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div className="comparison-empty">No CloudBlue subscriptions</div>
              )}
            </div>
            <div className="comparison-card m365-card">
              <div className="comparison-header m365-header-card">
                <span className="comparison-dot" style={{background: '#00a4ef'}}></span>
                M365 Partner Center
              </div>
              {comparisonData.m365?.items?.length > 0 ? (
                <table className="comparison-table">
                  <thead><tr><th>License</th><th>Qty</th></tr></thead>
                  <tbody>
                    {comparisonData.m365.items.map((item: any, i: number) => (
                      <tr key={i}>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                      </tr>
                    ))}
                    <tr className="comparison-total">
                      <td><strong>Total</strong></td>
                      <td><strong>{comparisonData.m365.total_qty}</strong></td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div className="comparison-empty">No M365 licenses</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Services Grid */}
      <div className="services-grid">
        {serviceTypes.map(serviceType => {
          const mappings = servicesView.services[serviceType] || [];
          if (mappings.length === 0) return null;

          return (
            <div key={serviceType} className="service-section">
              <div className="service-header" style={{ borderLeftColor: SERVICE_COLORS[serviceType] }}>
                <h2>{SERVICE_LABELS[serviceType]}</h2>
                <span className="mapping-count">{mappings.length} mapping{mappings.length !== 1 ? 's' : ''}</span>
              </div>

              {mappings.map(mapping => (
                <div key={mapping.id} className="mapping-card">
                  <div className="mapping-header">
                    <div className="mapping-info">
                      <span className="mapping-name">{mapping.external_name || 'Unnamed'}</span>
                      {mapping.external_id && <span className="mapping-id">{mapping.external_id}</span>}
                    </div>
                    <div className="mapping-actions">
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => setShowAddProduct(mapping.id)}
                      >
                        + Product
                      </button>
                      <button 
                        className="btn btn-sm btn-danger-outline"
                        onClick={() => handleDeleteMapping(mapping.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Add Product Form */}
                  {showAddProduct === mapping.id && (
                    <div className="add-product-form">
                      <div className="form-row">
                        <input
                          type="text"
                          className="input"
                          placeholder="Product Name"
                          value={productForm.product_name}
                          onChange={e => setProductForm({ ...productForm, product_name: e.target.value })}
                        />
                        <input
                          type="text"
                          className="input input-sm"
                          placeholder="SKU"
                          value={productForm.product_sku}
                          onChange={e => setProductForm({ ...productForm, product_sku: e.target.value })}
                        />
                        <input
                          type="number"
                          className="input input-xs"
                          placeholder="Qty"
                          value={productForm.quantity}
                          onChange={e => setProductForm({ ...productForm, quantity: parseInt(e.target.value) || 1 })}
                        />
                        <button className="btn btn-sm btn-primary" onClick={() => handleAddProduct(mapping.id)}>Add</button>
                        <button className="btn btn-sm btn-outline" onClick={() => setShowAddProduct(null)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Products List */}
                  {mapping.products.length > 0 ? (
                    <div className="products-list">
                      <table className="products-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>SKU</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Cycle</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {mapping.products.map(product => (
                            <tr key={product.id}>
                              <td>{product.product_name}</td>
                              <td className="text-muted">{product.product_sku || '-'}</td>
                              <td>{product.quantity}</td>
                              <td>{product.unit_price || '-'}</td>
                              <td className="text-muted">{product.billing_cycle || '-'}</td>
                              <td>
                                <button 
                                  className="btn-link btn-danger"
                                  onClick={() => handleDeleteProduct(product.id)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-products">No products added yet</div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {servicesView.total_services === 0 && (
          <div className="empty-state">
            <p>No service mappings configured for this customer.</p>
            <p>Click "Add Service Mapping" to start mapping services.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerServices;
